import React, { FunctionComponent, useEffect, useState } from "react";
import {
  BannerStatus,
  Dialog,
  BannerConsumer,
  BannerPlaceholder,
  BannerProvider,
  Button,
  ColumnType,
  Form,
  IBannerContext,
  TextInput,
  ValidationMessage
} from "@plex/react-components";
import { IViewController, useViewController } from "../ViewContext";
import "./UserDefinedSchemaEditor.scss";
import { getSchema } from "../NodeTypes/DataSchemas";
import {
  DocumentPrimitiveType,
  IDocumentObjectSchema,
  IDocumentObjectSchemaProperty
} from "../FlowDocument/FlowDocumentModel";
import { useUpdateNodeInternals } from "reactflow";
import { variableInPropertyName, variableOutPropertyName } from "./LoopVariableEditor";
import {
  applyAutoCreateResult,
  autoCreateNodes,
  autoCreateNodesForLoop,
  IAutoCreateResult,
  refreshNodeDataPropertiesFromSchema
} from "../Util/NodeUtil";
import { AddMenuNodePopulater } from "../AddNodeMenu/AddMenuNodePopulation";

export interface IUserDefinedSchemaPropertyAddDialogPropsState {
  show: boolean;
  schemaId: string;
}

export interface IUserDefinedSchemaPropertyAddDialogProps {
  onClose?: () => void;
}

export const UserDefinedSchemaPropertyAddDialog: FunctionComponent<IUserDefinedSchemaPropertyAddDialogProps> = (
  props
) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const {
    userDefinedSchemaDetailsDialogState,
    userDefinedSchemaPropertyAddDialogState,
    userDefinedSchemaPropertyDetailsDialogState,
    setUserDefinedSchemaPropertyDetailsDialogState
  }: IViewController = useViewController();
  const [nameState, setNameState] = useState<string>("");
  const [nameErrorState, setNameErrorState] = useState<string>("");
  useEffect(() => {
    if (userDefinedSchemaPropertyAddDialogState.show) {
      setNameState("");
      setNameErrorState("");
    }
  }, [userDefinedSchemaPropertyAddDialogState.show]);

  const validateName = (value: string): string => {
    if (value.trim() === "") {
      return "";
    }
    if (!/^[a-zA-Z0-9-_]+$/.test(value)) {
      return "Name must be alphanumeric, contain dashes or underscores.";
    }
    const schema = getSchema(userDefinedSchemaPropertyAddDialogState.schemaId) as unknown as IDocumentObjectSchema;
    const existingPropertyName = Object.keys(schema?.properties ?? {}).find(
      (propertyName) => propertyName.toLowerCase() === value.toLowerCase()
    );
    if (existingPropertyName) {
      return "A property with this name already exists. Please choose a different name.";
    }
    return "";
  };

  return (
    <Dialog
      show={userDefinedSchemaPropertyAddDialogState.show}
      onHide={props.onClose}
      title="Add Property"
      className="user-defined-schema-dialog"
    >
      <div style={{ height: "auto" }}>
        <BannerProvider>
          <BannerConsumer>
            {(bctx: IBannerContext) => {
              return (
                <Form
                  onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                    // Do not refresh the page when pressing enter!
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Form.Header>
                    <BannerPlaceholder style={{ marginTop: "20px", marginBottom: 0, marginLeft: 0, marginRight: 0 }} />
                  </Form.Header>
                  <Form.Body>
                    <Form.Section title="Details" layout={ColumnType.single}>
                      <Form.Row>
                        <Form.FieldPair labelText="Name" required={true}>
                          <TextInput
                            data-testid="user-defined-schema-property-add-name-input"
                            value={nameState}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const newValue = e.target.value;
                              setNameState(newValue);
                              setNameErrorState(validateName(newValue));
                            }}
                            placeholder="Enter name"
                          />
                          <ValidationMessage message={nameErrorState} />
                        </Form.FieldPair>
                      </Form.Row>
                    </Form.Section>
                  </Form.Body>
                  <Form.Footer>
                    <Form.Row>
                      <Button variance={"link"} as={"button"} type="button" onClick={props.onClose}>
                        Cancel
                      </Button>
                      <Button
                        data-testid="user-defined-schema-property-add-form-save"
                        variance={"primary"}
                        as={"button"}
                        type="button"
                        disabled={nameState.trim() === "" || nameErrorState !== ""}
                        onClick={() => {
                          const schema = getSchema(
                            userDefinedSchemaPropertyAddDialogState.schemaId
                          ) as unknown as IDocumentObjectSchema;
                          const existingPropertyName = Object.keys(schema.properties).find(
                            (propertyName) => propertyName.toLowerCase() === nameState.toLowerCase()
                          );
                          if (existingPropertyName) {
                            bctx.addMessage(
                              "A property with this name already exists. Please choose a different name.",
                              BannerStatus.warning
                            );
                            return;
                          }
                          schema.properties[nameState] = {
                            schema: DocumentPrimitiveType.string,
                            required: userDefinedSchemaDetailsDialogState.loopVariable
                          } as IDocumentObjectSchemaProperty;

                          if (userDefinedSchemaDetailsDialogState.loopVariable) {
                            const loopNode = AddMenuNodePopulater._reactFlowInstance.getNode(
                              userDefinedSchemaDetailsDialogState.loopNodeId!
                            );
                            if (loopNode) {
                              let autoCreateResult: IAutoCreateResult = {
                                nodes: [],
                                connections: [],
                                anchors: []
                              };
                              const variablesIsEnabled =
                                loopNode.data.nodeProperties.inputs[variableInPropertyName].enabled &&
                                loopNode.data.nodeProperties.outputs[variableOutPropertyName].enabled;
                              if (!variablesIsEnabled) {
                                loopNode.data.nodeProperties.inputs[variableInPropertyName].enabled = true;
                                loopNode.data.nodeProperties.outputs[variableOutPropertyName].enabled = true;
                                const loopNodeAutoCreateResult = autoCreateNodesForLoop(
                                  loopNode,
                                  AddMenuNodePopulater._reactFlowInstance.getEdges()
                                );
                                autoCreateResult = {
                                  nodes: loopNodeAutoCreateResult.nodes,
                                  connections: loopNodeAutoCreateResult.connections,
                                  anchors: loopNodeAutoCreateResult.anchors
                                };
                              }
                              const linkedLoopNodes = AddMenuNodePopulater._reactFlowInstance
                                .getNodes()
                                .filter((node) => node.data.nodeProperties.insideLoopNodeId === loopNode.id);

                              linkedLoopNodes.forEach((node) => {
                                const variablesInput = node.data.nodeProperties.inputs[variableInPropertyName];
                                if (variablesInput && !variablesInput.enabled) {
                                  variablesInput.enabled = true;
                                  const linkedNodeAutoCreateResult = autoCreateNodes(
                                    node,
                                    AddMenuNodePopulater._reactFlowInstance.getEdges()
                                  );
                                  autoCreateResult = {
                                    nodes: [...autoCreateResult.nodes, ...linkedNodeAutoCreateResult.nodes],
                                    connections: [
                                      ...autoCreateResult.connections,
                                      ...linkedNodeAutoCreateResult.connections
                                    ],
                                    anchors: [...autoCreateResult.anchors, ...linkedNodeAutoCreateResult.anchors]
                                  };
                                }
                              });

                              applyAutoCreateResult(
                                autoCreateResult,
                                AddMenuNodePopulater._reactFlowInstance,
                                updateNodeInternals,
                                () => {
                                  refreshNodeDataPropertiesFromSchema(
                                    schema.id,
                                    AddMenuNodePopulater._reactFlowInstance,
                                    updateNodeInternals
                                  );
                                }
                              );
                            }
                          }

                          if (props.onClose) {
                            props.onClose();
                          }

                          userDefinedSchemaPropertyDetailsDialogState.show = true;
                          userDefinedSchemaPropertyDetailsDialogState.schemaId = schema.id;
                          userDefinedSchemaPropertyDetailsDialogState.propertyName = nameState;
                          setUserDefinedSchemaPropertyDetailsDialogState({
                            ...userDefinedSchemaPropertyDetailsDialogState
                          });
                        }}
                      >
                        Ok
                      </Button>
                    </Form.Row>
                  </Form.Footer>
                </Form>
              );
            }}
          </BannerConsumer>
        </BannerProvider>
      </div>
    </Dialog>
  );
};
